import React, { Suspense, lazy } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate, useParams } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { PAYMENT_ENABLED } from './config/features';
import { Box, CircularProgress } from '@mui/material';
import { CookieConsentProvider } from './contexts/CookieConsentContext';
import CookieBanner from './components/CookieConsent/CookieBanner';
import BarionPixel from './components/Barion/BarionPixel';
import { trackPageView } from './services/analyticsService';
import { LegalAcceptanceProvider } from './contexts/LegalAcceptanceContext';
import TermsUpdateModal from './components/Legal/TermsUpdateModal';

// Context
import { AuthProvider } from './contexts/AuthContext';
import { LayoutProvider } from './contexts/LayoutContext';

// Components
import Header from './components/Layout/Header';
import Footer from './components/Layout/Footer';
import ProtectedRoute from './components/Auth/ProtectedRoute'; // ÚJ IMPORT: ProtectedRoute
import FeedbackWidget from './components/FeedbackWidget/FeedbackWidget';
import theme from './theme';

// A főoldal (LCP) marad közvetlen importtal; a többi oldal útvonalanként, igény szerint töltődik le (code splitting),
// így a kezdeti JS-csomag nem tartalmazza az összes oldalt és nagy könyvtárát (INP / LCP javítás).
import Home from './pages/Home';

const Login = lazy(() => import('./pages/Auth/Login'));
const Register = lazy(() => import('./pages/Auth/Register'));
const ServiceProviders = lazy(() => import('./pages/ServiceProviders'));
const ServiceProviderDetail = lazy(() => import('./pages/ServiceProviderDetail'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const AdminDashboard = lazy(() => import('./pages/Admin/AdminDashboard'));
const AdminStatsPage = lazy(() => import('./pages/Admin/AdminStatsPage'));
const ProviderEditPage = lazy(() => import('./pages/Admin/ProviderEditPage'));
const GalleryManager = lazy(() => import('./pages/Provider/GalleryManager'));
const AdminBlogPostsPage = lazy(() => import('./components/Admin/AdminBlogPostsPage')); // ÚJ IMPORT: AdminBlogPostsPage

// ÚJ: Blog oldalak importálása
const BlogListPage = lazy(() => import('./pages/Blog/BlogListPage'));
const BlogPostDetailPage = lazy(() => import('./pages/Blog/BlogPostDetailPage'));

const Favorites = lazy(() => import('./pages/Favorites'));
const Bookings = lazy(() => import('./pages/Bookings'));
const NotificationsPage = lazy(() => import('./pages/Notifications'));
const ProfileEdit = lazy(() => import('./pages/Profile/ProfileEdit'));
const Settings = lazy(() => import('./pages/Settings/Settings'));
const Messages = lazy(() => import('./pages/Messages'));
const ActivateAccount = lazy(() => import('./pages/Auth/ActivateAccount'));
const ForgotPassword = lazy(() => import('./pages/Auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/Auth/ResetPassword'));
const ClaimProviderInvite = lazy(() => import('./pages/Auth/ClaimProviderInvite'));
const ProviderInviteWelcome = lazy(() => import('./pages/Auth/ProviderInviteWelcome'));
const ConfirmEmailChange = lazy(() => import('./pages/Auth/ConfirmEmailChange'));
const DeclineProviderInvite = lazy(() => import('./pages/Auth/DeclineProviderInvite'));
const BookingDetails = lazy(() => import('./components/Booking/BookingDetails'));
const CartPage = lazy(() => import('./pages/Cart'));
const PaymentStatusPage = lazy(() => import('./pages/PaymentStatusPage'));
const ProviderFeaturedPage = lazy(() => import('./pages/Provider/ProviderFeaturedPage'));
const EarlyOfferPage = lazy(() => import('./pages/Provider/EarlyOfferPage'));
const EarlyOfferUnsubscribe = lazy(() => import('./pages/Auth/EarlyOfferUnsubscribe'));
const FeaturedPaymentSuccess = lazy(() => import('./pages/Provider/FeaturedPaymentSuccess'));
const FeaturedPaymentStatus = lazy(() => import('./pages/Provider/FeaturedPaymentStatus'));
const ProviderStatsPage = lazy(() => import('./pages/Provider/ProviderStatsPage'));
const WeddingPlannerDashboard = lazy(() => import('./pages/WeddingPlanner/WeddingPlannerDashboard'));
const WeddingEventDetail = lazy(() => import('./pages/WeddingPlanner/WeddingEventDetail'));
const RSVPPage = lazy(() => import('./pages/RSVP/RSVPPage'));
const ProviderLead = lazy(() => import('./pages/ProviderLead'));
const Reszletek = lazy(() => import('./pages/Reszletek'));
const ForumHome = lazy(() => import('./pages/Forum/ForumHome'));
const ForumCategory = lazy(() => import('./pages/Forum/ForumCategory'));
const ForumThread = lazy(() => import('./pages/Forum/ForumThread'));
const ForumCreate = lazy(() => import('./pages/Forum/ForumCreate'));
const ForumModeration = lazy(() => import('./pages/Admin/ForumModeration'));
const SocialRegisterComplete = lazy(() => import('./pages/Auth/SocialRegisterComplete'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const ProviderTerms = lazy(() => import('./pages/ProviderTerms'));
const CookiePolicy = lazy(() => import('./pages/CookiePolicy'));

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || 'not-configured';

const BookingDetailsWrapper = () => {
  const { id } = useParams();
  return <BookingDetails bookingId={parseInt(id)} />;
};

// Oldalváltáskor a kódcsomag betöltéséig látszik; a teljes magasság miatt a lábléc nem ugrik meg
const RouteFallback = () => (
  <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <CircularProgress />
  </Box>
);

function RouteTracker() {
  const location = useLocation();
  React.useEffect(() => {
    const noTrack = ['/admin', '/dashboard'].some(p => location.pathname.startsWith(p));
    if (!noTrack) trackPageView(location.pathname);
  }, [location]);
  return null;
}

function MainContent() {
  const location = useLocation();

  const isHomePage = location.pathname === '/';
  const noChrome = NO_CHROME_ROUTES.some(path => location.pathname.startsWith(path));

  const headerHeight = (isHomePage || noChrome) ? 0 : 64;

  return (
    <Box sx={{ flexGrow: 1, paddingTop: `${headerHeight}px` }}>
      <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/" element={<Home />} />
        {/* A Google-bejelentkezés (GSI script) csak itt kell: máshol nem töltődik be */}
        <Route path="/login" element={<GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}><Login /></GoogleOAuthProvider>} />
        <Route path="/register" element={<Register />} />
        <Route path="/szolgaltatok" element={<ServiceProviders />} />
        {/* SEO landing oldalak: a "helyszin" statikus szegmens megelőzi a :categorySlug-ot */}
        <Route path="/szolgaltatok/helyszin/:countySlug" element={<ServiceProviders />} />
        <Route path="/szolgaltatok/:categorySlug" element={<ServiceProviders />} />
        <Route path="/szolgaltatok/:categorySlug/:countySlug" element={<ServiceProviders />} />
        <Route path="/szolgaltato/:slug" element={<ServiceProviderDetail />} />
        <Route path="/galeria-kezeles" element={<GalleryManager />} />
        <Route path="/kedvencek" element={<Favorites />} />
        <Route path="/foglalasok" element={<Bookings />} />
        <Route path="/foglalasok/:id" element={<BookingDetailsWrapper />} />
        <Route path="/ertesitesek" element={<NotificationsPage />} />
        <Route path="/profil" element={<Navigate to="/beallitasok?tab=0" replace />} />
        <Route path="/beallitasok" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        <Route path="/messages" element={<Messages />} />
        <Route path="/activate" element={<ActivateAccount />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/szolgaltatoi-udvozlo/:token/:decline" element={<ProviderInviteWelcome />} />
        <Route path="/szolgaltatoi-udvozlo" element={<ProviderInviteWelcome />} />
        <Route path="/szolgaltatoi-meghivo" element={<ClaimProviderInvite />} />
        <Route path="/email-megerosites" element={<ConfirmEmailChange />} />
        <Route path="/meghivo-elutasitas/:token" element={<DeclineProviderInvite />} />
        <Route path="/meghivo-elutasitas" element={<DeclineProviderInvite />} />
        <Route path="/social-register" element={<SocialRegisterComplete />} />
        <Route path="/kosar" element={PAYMENT_ENABLED ? <CartPage /> : <Navigate to="/" replace />} />
        <Route path="/payment/status" element={<PaymentStatusPage />} />
         {/* Nyilvános Blog útvonalak */}
        <Route path="/blog" element={<BlogListPage />} />
        <Route path="/blog/:slug" element={<BlogPostDetailPage />} />
        <Route path="/erdeklodes" element={<ProviderLead />} />
        <Route path="/reszletek" element={<Reszletek />} />
        <Route path="/adatvedelmi-nyilatkozat" element={<PrivacyPolicy />} />
        <Route path="/felhasznalasi-feltetelek" element={<TermsOfService />} />
        <Route path="/partneri-aszf" element={<ProviderTerms />} />
        <Route path="/sutik" element={<CookiePolicy />} />
        {/* Fórum */}
        <Route path="/forum" element={<ForumHome />} />
        <Route path="/forum/uj-tema" element={<ForumCreate />} />
        <Route path="/forum/tema/:id" element={<ForumThread />} />
        <Route path="/forum/:categorySlug" element={<ForumCategory />} />

        {/* Védett útvonalak */}
        <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute requiredRole="ADMIN"><AdminDashboard /></ProtectedRoute>} />
        <Route path="/admin/providers/edit/:id" element={<ProtectedRoute requiredRole={["ADMIN", "PROVIDER"]}><ProviderEditPage /></ProtectedRoute>} />
        <Route path="/profil/szolgaltato" element={<ProtectedRoute requiredRole="PROVIDER"><ProviderEditPage /></ProtectedRoute>} />
        <Route path="/admin/blogposts" element={<ProtectedRoute requiredRole="ADMIN"><AdminBlogPostsPage /></ProtectedRoute>} />
        <Route path="/admin/forum" element={<ProtectedRoute requiredRole="ADMIN"><ForumModeration /></ProtectedRoute>} />
        <Route path="/admin/statisztika" element={<ProtectedRoute requiredRole="ADMIN"><AdminStatsPage /></ProtectedRoute>} />
        <Route path="/korai-ajanlat" element={<ProtectedRoute requiredRole="PROVIDER"><EarlyOfferPage /></ProtectedRoute>} />
        <Route path="/korai-ajanlat-leiratkozas" element={<EarlyOfferUnsubscribe />} />
        <Route path="/provider/featured" element={<ProtectedRoute requiredRole="PROVIDER"><ProviderFeaturedPage /> </ProtectedRoute>} />
        <Route path="/statisztika" element={<ProtectedRoute requiredRole="PROVIDER"><ProviderStatsPage /></ProtectedRoute>} />
        <Route path="/featured/payment-success" element={<FeaturedPaymentSuccess />} />
        <Route path="/featured/payment-status"  element={<ProtectedRoute requiredUserType="PROVIDER"><FeaturedPaymentStatus /></ProtectedRoute>}/>
        <Route path="/wedding-planner"element={<ProtectedRoute><WeddingPlannerDashboard /></ProtectedRoute>}/>
        <Route path="/wedding-planner/:eventId"element={<ProtectedRoute><WeddingEventDetail /></ProtectedRoute>}/>
        <Route path="/rsvp/:token" element={<RSVPPage />} />
        {/* 404 - Must be last */}
        <Route path="*" element={<Navigate to="/" replace />} />

      </Routes>
      </Suspense>
    </Box>
  );
}


// Oldalak amelyeken nem jelenik meg a fejlec es labléc
const NO_CHROME_ROUTES = ['/erdeklodes', '/rsvp', '/reszletek'];

function AppLayout() {
  const location = useLocation();
  const noChrome = NO_CHROME_ROUTES.some(path => location.pathname.startsWith(path));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {!noChrome && <Header />}
      <MainContent />
      {!noChrome && <Footer />}
      {!noChrome && <FeedbackWidget />}
      <BarionPixel />
      <CookieBanner />
      <TermsUpdateModal />
      <RouteTracker />
    </Box>
  );
}

function App() {
  return (
    <CookieConsentProvider>
    <HelmetProvider>
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <LegalAcceptanceProvider>
          <Router>
            <LayoutProvider>
              <AppLayout />
            </LayoutProvider>
          </Router>
        </LegalAcceptanceProvider>
      </AuthProvider>
    </ThemeProvider>
    </HelmetProvider>
    </CookieConsentProvider>
  );
}

export default App;
